import { useEffect, useState } from 'react';
import {
  IonIcon,
  IonItem,
  IonItemOption,
  IonItemOptions,
  IonItemSliding,
  IonLabel,
  IonList,
  IonSkeletonText,
  IonSpinner,
} from '@ionic/react';
import { trash } from 'ionicons/icons';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../../../store/types';
import PaymentManager from '../../../../services/api/PaymentManager';
import { IModal } from '../../router/ModalRouter';
import { useTranslation } from 'react-i18next';
import { Modals } from '../../exports/Enums';
import MasterCardIcon from '../../../../assets/icons/payment-methods/MasterCardIcon';
import VisaIcon from '../../../../assets/icons/payment-methods/VisaIcon';
import AmericanExpressIcon from '../../../../assets/icons/payment-methods/AmericanExpressIcon';
import GooglePayIcon from '../../../../assets/icons/payment-methods/GooglePayIcon';
import ApplePayIcon from '../../../../assets/icons/payment-methods/ApplePayIcon';
import PayByBankIcon from '../../../../assets/icons/payment-methods/PayByBankIcon';
import { RawPaymentMethod, RawStoredPaymentMethod } from '@adyen/adyen-web';
import classnames from 'classnames';

interface Props {
  openModal: IModal['openModal'];
  isBack?: boolean;
}

export default function AdyenPaymentMethodsList({ openModal, isBack }: Props) {
  const { i18n } = useTranslation();
  const { company } = useSelector((state: StoreInterface) => state.initialData);

  const [storedMethods, setStoredMethods] = useState<RawStoredPaymentMethod[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<RawPaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const fetchPaymentMethods = async () => {
    if (!company?.id) return;

    setLoading(true);
    const { success, response } = await PaymentManager.getAdyenPaymentMethodsConfig({
      company_id: company.id,
      shopper_locale: i18n.language,
      browser_info: {
        acceptHeader: navigator.userAgent,
        colorDepth: screen.colorDepth,
        javaEnabled: false,
        javaScriptEnabled: true,
        language: navigator.language,
        screenHeight: screen.height,
        screenWidth: screen.width,
        timeZoneOffset: new Date().getTimezoneOffset(),
        userAgent: navigator.userAgent,
      },
    });

    if (success) {
      setStoredMethods(response.data.storedPaymentMethods ?? []);
      setPaymentMethods(response.data.paymentMethods ?? []);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchPaymentMethods();
  }, [company?.id]);

  useEffect(() => {
    if (isBack) {
      fetchPaymentMethods();
    }
  }, [isBack]);

  const deleteStoredMethod = async (id: string) => {
    setDeletingId(id);
    const { success } = await PaymentManager.deleteAdyenPaymentMethod(id as any, {
      company_id: company?.id,
    });

    if (success) {
      setStoredMethods((prev) => prev.filter((m) => m.id !== id));
    }

    setDeletingId(null);
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-4 p-sm">
        <IonSkeletonText animated className="w-full h-6" />
        <IonSkeletonText animated className="w-full h-6" />
        <IonSkeletonText animated className="w-full h-6" />
      </div>
    );
  }

  return (
    <IonList>
      {storedMethods.map((method) => (
        <IonItemSliding key={method.id}>
          <IonItem className="flex justify-between">
            <IonLabel>
              <h6 className="!font-medium">{method.lastFour ? `**** **** **** ${method.lastFour}` : method.name}</h6>
            </IonLabel>
            <div className="flex items-center gap-2">
              <BrandIcon brand={method.brand} />
            </div>
          </IonItem>
          <IonItemOptions>
            <IonItemOption color="danger" onClick={() => deleteStoredMethod(method.id)}>
              {deletingId === method.id ? <IonSpinner name="crescent" className="w-5 h-5" /> : <IonIcon icon={trash} />}
            </IonItemOption>
          </IonItemOptions>
        </IonItemSliding>
      ))}

      {paymentMethods.map((method) => (
        <IonItem
          key={method.type}
          button={method.type === 'scheme'}
          onClick={method.type === 'scheme' ? () => openModal({ id: Modals.AddAdyenCard }) : undefined}
        >
          <IonLabel>
            <h6 className="!font-medium">{method.name}</h6>
          </IonLabel>
          <div className="flex items-center gap-2">
            <PaymentMethodIcons method={method} />
          </div>
        </IonItem>
      ))}
    </IonList>
  );
}

function BrandIcon({ brand }: { brand?: string }) {
  const className = 'h-[14px]';

  switch (brand) {
    case 'visa':
      return <VisaIcon className={className} />;
    case 'mc':
    case 'maestro':
      return <MasterCardIcon className={className} />;
    case 'amex':
      return <AmericanExpressIcon className={className} />;
    default:
      return null;
  }
}

function PaymentMethodIcons({ method }: { method: RawPaymentMethod }) {
  const iconClassName = 'h-[14px]';

  if (method.type === 'scheme' && method.brands) {
    return (
      <div className="flex items-center gap-2">
        {method.brands.map((brand) => (
          <BrandIcon key={brand} brand={brand} />
        ))}
      </div>
    );
  }

  switch (method.type) {
    case 'applepay':
      return <ApplePayIcon className={iconClassName} />;
    case 'googlepay':
      return <GooglePayIcon className={classnames(iconClassName, 'h-5')} />;
    case 'paybybank':
      return <PayByBankIcon className={iconClassName} />;
    default:
      return null;
  }
}
